/**
 * ScheduleTourForm — Weidner Classic
 *
 * Tower 12 schedule-a-tour body (https://www.tower12.com/schedule-a-tour),
 * under the shared HeroSolidBG "SCHEDULE A TOUR" banner:
 *
 *   1. Light gray full-bleed section with generous vertical padding
 *   2. Centered white Multihub tour-scheduler card (max ~700px)
 *   3. Secondary heading + selected-state accents (brand secondary)
 *
 * The rise dark parallax form card and PropertyInfoPanel info-card row
 * are omitted — pet policy / fees / Contact·Visit·Hours live in the
 * Header hamburger drawer on Tower 12, not on this page.
 *
 * Maintenance checklist when re-syncing with the rise parent:
 *   1. Diff against `@vibesites/theme-rise/components/ScheduleTourForm.tsx`.
 *   2. Do not reintroduce the parallax-hero form card or the horizontal
 *      PropertyInfoPanel info-card row.
 *   3. Keep the light gray section + centered white scheduler card.
 *   4. Pin a light-card ink palette with literal dark colors — property
 *      `--vibe-color-text` is often white (dark chrome) and will make
 *      dates/times/inputs invisible on the white Multihub card if reused.
 *      Gold selected/CTA states; readable muted disabled Continue.
 *   5. Run `pnpm run lint:schema` and `pnpm run typecheck`.
 */

import type { ComponentContentSchema } from '@vibesites/runtime';
import {
  useBlockSlot,
  SectionContextProvider,
  EditableToken,
} from '@vibesites/runtime/react';
import { useScrollReveal } from '../hooks/useScrollReveal';

// ============================================================================
// Component
// ============================================================================

export function ScheduleTourForm() {
  const formSlot = useBlockSlot('scheduletourform.form_block', {
    defaultValue: 'multihub-tour-scheduler',
  });

  const sectionRef = useScrollReveal<HTMLElement>({ threshold: 0.1 });

  return (
    <>
      <section ref={sectionRef} className="stf-section" aria-label="Schedule a tour">
        <div className="stf-section__inner">
          <div className="stf-form-wrap">
            <EditableToken path="scheduletourform.form_block" as="div">
              {formSlot ? (
                <SectionContextProvider sectionId={formSlot.sectionId}>
                  <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                </SectionContextProvider>
              ) : (
                <div className="stf-form-empty">
                  <p>No form block selected</p>
                </div>
              )}
            </EditableToken>
          </div>
        </div>
      </section>

      <style>{`
        /*
         * Light-card surface palette.
         * Property --vibe-color-text is often white (dark chrome). On this
         * white Multihub card that makes dates / times / inputs invisible —
         * pin dark ink and muted gray with literal colors, not vibe text tokens.
         */
        .stf-section {
          background: #f5f5f5;
          padding: 3.5rem 1.25rem 5rem;
        }
        @media (min-width: 768px) {
          .stf-section {
            padding: 5.25rem 1.875rem 9rem;
          }
        }

        .stf-section__inner {
          width: 100%;
          max-width: 44rem;
          margin: 0 auto;
        }

        .stf-form-wrap {
          width: 100%;
          color: #1a1a1a;
          --vibe-color-text: #1a1a1a;
          --vibe-color-text-muted: #6b7280;
          --vibe-color-background: #ffffff;
          --vibe-color-border: #e5e7eb;
          --foreground: #1a1a1a;
          --muted-foreground: #6b7280;
          --background: #ffffff;
          --border: #e5e7eb;
          --primary: var(--vibe-color-secondary);
          --primary-foreground: var(--vibe-color-text-on-secondary);
          --secondary: var(--vibe-color-secondary);
          --secondary-foreground: var(--vibe-color-text-on-secondary);
        }

        /* Multihub card chrome */
        .stf-form-wrap [class*="shadow-xl"] {
          max-width: 100% !important;
          border-radius: 1rem !important;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08) !important;
          background: #ffffff !important;
          color: #1a1a1a !important;
        }

        /* Secondary left-aligned display heading */
        .stf-form-wrap h3 {
          color: var(--vibe-color-secondary) !important;
          font-family: var(--vibe-font-heading) !important;
          font-size: 1.75rem !important;
          font-weight: 700 !important;
          text-align: left !important;
          text-transform: none !important;
          letter-spacing: normal !important;
          margin: 0 0 1.5rem !important;
        }

        /* Force dark readable copy across the wizard (dates, times, labels) */
        .stf-form-wrap p,
        .stf-form-wrap span,
        .stf-form-wrap h4,
        .stf-form-wrap label,
        .stf-form-wrap li,
        .stf-form-wrap td,
        .stf-form-wrap th {
          color: #1a1a1a !important;
        }
        .stf-form-wrap .text-foreground {
          color: #1a1a1a !important;
        }
        .stf-form-wrap .text-muted-foreground,
        .stf-form-wrap .text-gray-400 {
          color: #6b7280 !important;
        }
        .stf-form-wrap .text-gray-900 {
          color: #1a1a1a !important;
        }

        .stf-form-wrap input,
        .stf-form-wrap select,
        .stf-form-wrap textarea {
          color: #1a1a1a !important;
          background-color: #ffffff !important;
          border-color: #d1d5db !important;
        }
        .stf-form-wrap input::placeholder,
        .stf-form-wrap textarea::placeholder {
          color: #9ca3af !important;
          opacity: 1;
        }

        /* Default chips / nav chevrons — dark on white */
        .stf-form-wrap button {
          color: #1a1a1a !important;
        }

        /* Muted Back / disabled-day labels */
        .stf-form-wrap button.text-muted-foreground,
        .stf-form-wrap .text-muted-foreground {
          color: #6b7280 !important;
        }

        /* Selected day / time / CTA — secondary fill, on-secondary label */
        .stf-form-wrap button.bg-primary,
        .stf-form-wrap .bg-primary,
        .stf-form-wrap button[class*="bg-primary"] {
          background-color: var(--vibe-color-secondary) !important;
          color: var(--vibe-color-text-on-secondary) !important;
          border-color: var(--vibe-color-secondary) !important;
        }
        .stf-form-wrap .text-primary {
          color: var(--vibe-color-secondary) !important;
        }
        .stf-form-wrap .text-primary-foreground,
        .stf-form-wrap button.bg-primary *,
        .stf-form-wrap button[class*="bg-primary"] * {
          color: var(--vibe-color-text-on-secondary) !important;
        }
        .stf-form-wrap .border-primary {
          border-color: var(--vibe-color-secondary) !important;
        }

        /* Disabled Continue — dark label on light gray (readable) */
        .stf-form-wrap button.bg-primary:disabled,
        .stf-form-wrap button.bg-primary[disabled],
        .stf-form-wrap button.bg-primary.opacity-50,
        .stf-form-wrap button.bg-primary[class*="opacity-50"],
        .stf-form-wrap button[class*="bg-primary"][class*="opacity-50"] {
          background-color: #e5e7eb !important;
          color: #4b5563 !important;
          opacity: 1 !important;
          cursor: not-allowed !important;
        }
        .stf-form-wrap button.bg-primary:disabled *,
        .stf-form-wrap button.bg-primary[class*="opacity-50"] * {
          color: #4b5563 !important;
        }

        /* Unselected time slots — bordered white chips with dark labels */
        .stf-form-wrap button.bg-background,
        .stf-form-wrap button[class*="bg-background"] {
          background-color: #ffffff !important;
          color: #1a1a1a !important;
          border-color: #d1d5db !important;
        }

        .stf-form-empty {
          background: #ffffff;
          border-radius: 1rem;
          padding: 2rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          text-align: center;
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: #6b7280;
        }
        .stf-form-empty p {
          margin: 0;
        }

        /* Scroll reveal */
        .stf-section .stf-form-wrap {
          transition:
            opacity 0.7s cubic-bezier(0.22, 1, 0.36, 1),
            transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
        }
        .stf-section.reveal--hidden .stf-form-wrap {
          opacity: 0;
          transform: translateY(1.25rem);
        }
        .stf-section.reveal--visible .stf-form-wrap {
          opacity: 1;
          transform: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .stf-section.reveal--hidden .stf-form-wrap {
            opacity: 1;
            transform: none;
            transition: none;
          }
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ScheduleTourForm',
  displayName: 'Schedule A Tour Form',
  tokens: {
    'scheduletourform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-tour-scheduler',
      acceptedBlockCategories: ['Forms'],
    },
  },
};

(ScheduleTourForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ScheduleTourForm;
